/* eslint-disable array-callback-return */
import { PageLayout } from "components/layouts";
import { hasRole } from "config/auth";
import { NotificationConsumer } from "contexts/NotificationContext";
import NewRequestQuoteProvider from "contexts/RequestQuoteContext";
import { CashManagementRoutes } from "domains/CashManagement/routes";
import { CatalogRoutes } from "domains/Catalog/routes";
import { CourseRoutes } from "domains/Course/routes";
import { DashRoutes } from "domains/Dashboard/screens/routes";
import ErrorPage from "domains/Errors";
import { FidelityRoutes } from "domains/Fidelity/routes";
import { GiftRoutes } from "domains/Gift/routes";
import NewJobProvider from "domains/Job/context";
import { Confirmation, Login, Logout, NewPassword, ResetPassword } from "domains/Login/screens";
import { RedirectPage } from "domains/Login/screens/RedirectPage";
import { NotificationRoutes } from "domains/Notification/routes";
import { receiverRoutes } from "domains/Receiver/routes";
import { RegisterRoutes } from "domains/Registration/routes";
import Register from "domains/Registration/screen";
import { RequestRoutes } from "domains/Request/routes";
import { TaskRoutes } from "domains/Task/routes";
import { UserRoutes } from "domains/Users/routes";
import { useAuthState } from "hooks";
import React from "react";
import { Route, Routes } from "react-router-dom";
import { Loader } from "ui/Loader";
import ErrorBoundary from "./ErrorBoundary";

export interface IAllPrivateRoutes {
  label: string;
  pathName: string;
  routeElement: React.ReactElement | React.ReactNode | null;
  roles?: string[];
  rights?: string[];
}

export const AllPrivateRoutes: IAllPrivateRoutes[] = [
  UserRoutes,
  TaskRoutes,
  DashRoutes,
  CatalogRoutes,
  NotificationRoutes,
  RequestRoutes,
  receiverRoutes,
  GiftRoutes,
  RegisterRoutes,
  CashManagementRoutes,
  FidelityRoutes,
].flat();

export function PrivateRoutes({ user }: any) {
  return (
    <Routes>
      <NotificationConsumer>
        <NewJobProvider>
          <NewRequestQuoteProvider>
            <Route element={<PageLayout />}>
              {AllPrivateRoutes.map(({ routeElement, pathName, roles }: IAllPrivateRoutes, index) => {
                const RouteElement = routeElement as any;
                return hasRole(user, roles) && <Route key={index} path={pathName} element={<RouteElement />} />;
              })}
            </Route>
          </NewRequestQuoteProvider>
        </NewJobProvider>
      </NotificationConsumer>
      <Route path="*" element={<ErrorPage />} />
      <Route path="login" element={<Login />} />
      <Route path="redirect" element={<RedirectPage />} />
      <Route path="account/confirmation" element={<Confirmation />} />
      <Route path="forget/password" element={<ResetPassword />} />
      <Route path="reset/password" element={<NewPassword />} />
      <Route path="logout" element={<Logout />} />
    </Routes>
  );
}

export function AuthentificationRoutes() {
  if (process.env.REACT_APP_REDIRECT === "false") {
    return (
      <Routes>
        <Route path="register" element={<Register />} />
        <Route path="*" element={<Login />} />
        <Route path="login" element={<Login />} />
        <Route path="account/confirmation" element={<Confirmation />} />
        <Route path="logout" element={<Logout />} />
        <Route path="forget/password" element={<ResetPassword />} />
        <Route path="reset/password" element={<NewPassword />} />
      </Routes>
    );
  }
  return <Route path="*" element={<RedirectPage />} />;
}

export const AppRoute = () => {
  const [isAuthenticated, user, { loading }] = useAuthState();
  if (!isAuthenticated && loading) {
    return <Loader size={60} />;
  }

  return isAuthenticated && process.env.REACT_APP_REDIRECT === "false" ? (
    <ErrorBoundary>
      <PrivateRoutes user={user} />
    </ErrorBoundary>
  ) : (
    <ErrorBoundary>
      <AuthentificationRoutes />
    </ErrorBoundary>
  );
};

export default AppRoute;
